import React, { useState, useEffect } from "react";
import {
    SummaryNoCoverage,
    SummaryGov,
    Private,
    SummaryPrivateNotReturned,
    SummaryFA,
    SummaryCurae,
} from "@coverme/cards";
import { doesProvidedCoverageMatchesVerified } from "../../../services/coverageService";
import "../../../components/InterviewCoverageSummary/InterviewCoverageSummary.scss";
import { connect } from "react-redux";
import * as interviewActions from "../../../actions/interviewActions";
import { cloneDeep } from "lodash-es";
import { LoadingBar } from "../../../components/LoadingComponents/LoadingBar";
import InterviewCoverageForm from "../../../components/InterviewCoverageSummary/InterviewCoverageForm";
import { Formik } from "formik";
import { coverageSummaryValidationSchema } from "../../../components/InterviewCoverageSummary/validationSchema";
import { WizardButtonsField } from "../Components/WizardButtons";
import { YesNoButtons } from "../../../components/FormComponents/YesNoButtons";
import * as formsService from "../../../services/formsService";

function createInitialValues(coverage) {
    let initialValues = {
        provideManualCoverage: undefined,
        provideManualSupplementalCoverage: undefined,
        coverageConfirmed: undefined,
        supplementalCoverageConfirmed: undefined,
        coverageForm: {
            companyName:
                coverage.primaryCoverage.typeOfCoverage == "Other"
                    ? coverage.primaryCoverage.companyName
                    : coverage.primaryCoverage.typeOfCoverage,
            address: "",
            phoneNumber: "",
            plan: "",
            groupNumber: "",
            employer: "",
            memberId: coverage.primaryCoverage.memberNumber,
            copaysEr: "",
            copaysOffice: "",
            copaysSpecialist: "",
            deductibleRemaining: "",
            outOfPocketRemaining: "",
        },
        supplementalCoverageForm: {
            companyName: "",
            address: "",
            phoneNumber: "",
            plan: "",
            groupNumber: "",
            employer: "",
            memberId: "",
        },
    };

    if (coverage && coverage?.coverageSource && coverage.coverageSource?.name === "Interview") {
        // get the first item of coverage summary
        if (coverage.coverageSummary?.personCoverages && coverage.coverageSummary.personCoverages.length > 0) {
            const collectedCoverage = coverage.coverageSummary.personCoverages.filter(
                x => x.coverageSource.name === "Interview" && x.isPrimaryCoverage == true
            )[0];
            const collectedSupplementalCoverage = coverage.coverageSummary.personCoverages.filter(
                x => x.coverageSource.name === "Interview" && x.isPrimaryCoverage == false
            )[0];

            initialValues = {
                provideManualCoverage: true,
                provideManualSupplementalCoverage:
                    collectedSupplementalCoverage !== null && collectedSupplementalCoverage !== undefined,
                coverageConfirmed: collectedCoverage !== null && collectedCoverage !== undefined,
                supplementalCoverageConfirmed:
                    collectedSupplementalCoverage !== null && collectedSupplementalCoverage !== undefined,
                coverageForm: {
                    companyName: collectedCoverage.companyName,
                    address: collectedCoverage.address,
                    phoneNumber: collectedCoverage.phoneNumber,
                    plan: collectedCoverage.plan,
                    groupNumber: collectedCoverage.groupNumber,
                    employer: collectedCoverage.employer,
                    memberId: collectedCoverage.memberId,
                    copaysEr: collectedCoverage?.copay?.erInPlanNetwork,
                    copaysOffice: collectedCoverage?.copay?.office,
                    copaysSpecialist: collectedCoverage?.copay?.specialistInPlanNetwork,
                    deductibleRemaining: collectedCoverage?.remainingDeductible?.inPlanNetwork,
                    outOfPocketRemaining: collectedCoverage?.remainingOutOfPocket?.inPlanNetwork,
                },

                supplementalCoverageForm: {
                    companyName: collectedSupplementalCoverage?.companyName,
                    address: collectedSupplementalCoverage?.address,
                    phoneNumber: collectedSupplementalCoverage?.phoneNumber,
                    plan: collectedSupplementalCoverage?.plan,
                    groupNumber: collectedSupplementalCoverage?.groupNumber,
                    employer: collectedSupplementalCoverage?.employer,
                    memberId: collectedSupplementalCoverage?.memberId,
                },
            };
        }
    }

    return initialValues;
}

function areAllObjectsInArrayEmptyOrNull(sourceArray, except = []) {
    if (sourceArray == null) {
        return true;
    }

    if (sourceArray.length === 0) {
        return true;
    }

    for (const item of sourceArray) {
        if (!isEmptyOrHasOnlyNulls(item, except)) {
            return false;
        }
    }

    return true;
}

const InterviewCoverageSummary = ({
    coverage,
    interview,
    getIsFullyCovered,
    setPersonCoverage,
    deletePersonCoverage,
    getCoverage,
    updateCoverage,
    navigateToNextPage,
    navigateToPreviousPage,
    setContinueAfterCoverage,
    getPreviouslyObtainedCoverage,
    updateEncounterStatus,
    person,
    patientFullName,
    customerId,
}) => {
    const thePatient = patientFullName || "the patient";
    useEffect(() => {
        if (interview.ui.coverage.status === "complete") {
            if (coverage?.coverageSource?.name === "Interview") {
                getPreviouslyObtainedCoverage(interview?.globalId, customerId);
            }
        }
    }, [interview.ui.coverage.status]);

    const initialValues = createInitialValues(coverage);
    const hasConflicts = !doesProvidedCoverageMatchesVerified(coverage);
    const cards = coverage && coverage.primaryCoverage.typeOfCoverage !== "Other" && getCards(coverage, thePatient);

    const onSubmit = formData => {
        // formData.personId = interview.entry.patient.personId;
        let personCoverages = formData.personCoverage;
        return setPersonCoverage(personCoverages).then(() => {
            let interviewCoverage = cloneDeep(interview.coverage);
            interviewCoverage.coverageSource = formData.personCoverage[0].coverageSource;
            interviewCoverage.provideManualCoverage = formData.provideManualCoverage;
            interviewCoverage.provideManualSupplementalCoverage = formData.provideManualSupplementalCoverage;
            interviewCoverage.coverageConfirmed = formData.coverageConfirmed;
            interviewCoverage.supplementalCoverageConfirmed = formData.supplementalCoverageConfirmed;
            return updateCoverage(interviewCoverage, interview.encounterId).then(() => {
                getCoverage(interview.encounterId);
                getIsFullyCovered(interview.encounterId);
                return onPostSubmit(formData);
            });
        });
    };

    function onPostSubmit(values) {
        let doesHaveCoverage =
            interview.coverage.hasCoverage &&
            interview.coverage.primaryCoverage.typeOfCoverage != null &&
            interview.coverage.coverageSummary.personCoverages?.length > 0;
        if (interview.coverage.isFullyCovered) {
            // mark the encounter as complete
            updateEncounterStatus(interview.encounterId, "Complete");
            navigateToNextPage(true, true);
        }

        if (doesHaveCoverage) {
            setContinueAfterCoverage(false);
            setTimeout(() => navigateToNextPage(false), 500);
        } else {
            navigateToNextPage(false, false);
        }
    }

    const deleteProvidedPersonCoverage = coverage => {
        deletePersonCoverage(customerId, interview.entry.patient.personId);
        getIsFullyCovered(interview.encounterId);
    };

    const removeManualCoverageProvided = () => {
        deleteProvidedPersonCoverage({
            healthInsuranceType: {
                name: "Insurance",
            },
            coverageSource: {
                name: "Interview",
            },
        });
    };

    const removeManualSupplementalCoverageProvided = () => {
        deleteProvidedPersonCoverage({
            healthInsuranceType: {
                name: "Insurance",
            },
            coverageSource: {
                name: "Interview",
            },
        });
    };

    const onFormSubmit = (data, a, b, c) => {
        if (!data.provideManualCoverage) {
            return onPostSubmit();
        }

        let personCoverageForm = {
            provideManualCoverage: data.provideManualCoverage,
            provideManualSupplementalCoverage: data.provideManualSupplementalCoverage,
            coverageConfirmed: data.coverageConfirmed,
            supplementalCoverageConfirmed: data.supplementalCoverageConfirmed,
            personCoverage: [
                {
                    healthInsuranceType: {
                        name: "Insurance",
                    },
                    personId: person.data[interview.entry.patient.personId].globalId,
                    isPrimaryCoverage: true,
                    companyName: data.coverageForm.companyName,
                    address: data.coverageForm.address,
                    phoneNumber: data.coverageForm.phoneNumber,
                    plan: data.coverageForm.plan,
                    groupNumber: data.coverageForm.groupNumber,
                    employer: data.coverageForm.employer,
                    memberId: data.coverageForm.memberId,
                    copay: {
                        erInPlanNetwork: data.coverageForm.copaysEr,
                        office: data.coverageForm.copaysOffice,
                        specialistInPlanNetwork: data.coverageForm.copaysSpecialist,
                    },
                    remainingDeductible: {
                        inPlanNetwork: data.coverageForm.deductibleRemaining,
                    },
                    remainingOutOfPocket: {
                        inPlanNetwork: data.coverageForm.outOfPocketRemaining,
                    },
                    coverageSource: {
                        name: "Interview",
                    },
                },

                {
                    healthInsuranceType: {
                        name: "Insurance",
                    },
                    isPrimaryCoverage: false,
                    personId: person.data[interview.entry.patient.personId].globalId,
                    companyName: data.supplementalCoverageForm.companyName,
                    address: data.supplementalCoverageForm.address,
                    phoneNumber: data.supplementalCoverageForm.phoneNumber,
                    plan: data.supplementalCoverageForm.plan,
                    groupNumber: data.supplementalCoverageForm.groupNumber,
                    employer: data.supplementalCoverageForm.employer,
                    memberId: data.supplementalCoverageForm.memberId,
                    coverageSource: {
                        name: "Interview",
                    },
                    copay: {
                        erInPlanNetwork: "",
                        office: "",
                        specialistInPlanNetwork: "",
                    },
                    remainingDeductible: {
                        inPlanNetwork: "",
                    },
                    remainingOutOfPocket: {
                        inPlanNetwork: "",
                    },
                },
            ],
        };
        return onSubmit(personCoverageForm);
    };

    return (
        <div className="wizard-page">
            <Formik
                initialValues={initialValues}
                //onSubmit={() => Promise.resolve()}
                validationSchema={coverageSummaryValidationSchema}
                render={props => {
                    const { values, setFieldValue, dirty, isValid } = props;
                    const isRequired = formsService.isRequired.bind(this, coverageSummaryValidationSchema, values);
                    return (
                        <>
                            <div className="interview-coverage-summary">
                                {cards.length > 0 && cards}
                                {cards.length === 0 && (
                                    <SummaryNoCoverage
                                        title={"No Health Coverage Found"}
                                        subtitle={`Available information suggests that ${thePatient} has no current insurance.`}
                                    />
                                )}
                            </div>
                            <div className="interview-continue interview-coverage-inconsistency">
                                <h3 className="interview-continue__title">
                                    {coverage.primaryCoverage.typeOfCoverage === "Other" &&
                                        "We are unable to verify information for the Insurance mentioned. You can use the form below to provide any additional details."}

                                    {coverage.primaryCoverage.typeOfCoverage !== "Other" &&
                                        "Coverage identified does not match. Please contact insurance company for further details."}
                                </h3>
                                <div className="interview-continue interview-coverage-inconsistency">
                                    <YesNoButtons
                                        name={"provideManualCoverage"}
                                        title={"Do you wish to provide further coverage information?"}
                                        setFieldValue={setFieldValue}
                                        isRequired={isRequired}
                                        onFieldChange={isSelected => {
                                            if (!isSelected) {
                                                removeManualCoverageProvided();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            {values.provideManualCoverage && (
                                <div className="interview-continue interview-coverage-inconsistency">
                                    <YesNoButtons
                                        name={"coverageConfirmed"}
                                        title={`Does ${thePatient} have coverage?`}
                                        setFieldValue={setFieldValue}
                                        isRequired={isRequired}
                                        onFieldChange={isSelected => {
                                            if (!isSelected) {
                                                removeManualCoverageProvided();
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            {values.provideManualCoverage && values.coverageConfirmed && (
                                <div className="interview-coverage-collected-form">
                                    <h3 className="interview-coverage-collected-form__title">
                                        Coverage Collected Information
                                    </h3>
                                    <InterviewCoverageForm baseKey="coverageForm" {...props} />
                                </div>
                            )}
                            {values.coverageConfirmed && values.provideManualCoverage && (
                                <div className="interview-continue interview-coverage-inconsistency">
                                    <h3 className="interview-continue__title">Supplemental Coverage Information</h3>
                                    <div className="interview-continue interview-coverage-inconsistency">
                                        <YesNoButtons
                                            name={"provideManualSupplementalCoverage"}
                                            title={"Do you wish to provide supplemental coverage information?"}
                                            setFieldValue={setFieldValue}
                                            isRequired={isRequired}
                                            onFieldChange={isSelected => {
                                                if (!isSelected) {
                                                    removeManualSupplementalCoverageProvided();
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            {values.provideManualSupplementalCoverage &&
                                values.coverageConfirmed &&
                                values.provideManualCoverage && (
                                    <div className="interview-continue interview-coverage-inconsistency">
                                        <YesNoButtons
                                            name={"supplementalCoverageConfirmed"}
                                            title={`Does ${thePatient} have supplemental coverage?`}
                                            setFieldValue={setFieldValue}
                                            isRequired={isRequired}
                                            onFieldChange={isSelected => {
                                                if (!isSelected) {
                                                    removeManualSupplementalCoverageProvided();
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            {values.provideManualSupplementalCoverage &&
                                values.supplementalCoverageConfirmed &&
                                values.coverageConfirmed &&
                                values.provideManualCoverage && (
                                    <div className="interview-coverage-collected-form">
                                        <h3 className="interview-coverage-collected-form__title">
                                            Supplemental Coverage Collected Information
                                        </h3>
                                        <InterviewCoverageForm
                                            baseKey="supplementalCoverageForm"
                                            isPrimaryCoverage={false}
                                            {...props}
                                        />
                                    </div>
                                )}

                            {values.provideManualCoverage && (
                                <div className="required_field_indicator_explained">Indicates required field</div>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
};

export const InterviewCoverageSummaryContainer = connect(
    state => ({
        coverage: state.interview.coverage,
        interview: state.interview,
        patientFullName: state.interview.entry.patient.fullName,
        person: state.person,
        customerId: state.customer?.data?.customer?.GlobalId,
    }),
    dispatch => ({
        getIsFullyCovered: encounterId => dispatch(interviewActions.getIsFullyCovered(encounterId)),
        setPersonCoverage: interview => dispatch(interviewActions.setPersonCoverage(interview)),
        deletePersonCoverage: (customerId, personId) =>
            dispatch(interviewActions.deleteManuallyProvidedCoverage(customerId, personId)),
        getCoverage: encounterId => dispatch(interviewActions.getCoverage(encounterId)),
        updateCoverage: (data, encounterId) => dispatch(interviewActions.setCoverage(data, encounterId)),
        setContinueAfterCoverage: value => dispatch(interviewActions.setContinueAfterCoverage(value)),
        getPreviouslyObtainedCoverage: (externalId, customerId) =>
            dispatch(interviewActions.getPreviouslyObtainedCoverage(externalId, customerId)),
        updateEncounterStatus: (encounterId, status) =>
            dispatch(interviewActions.updateEncounterStatus(encounterId, status)),
    })
)(InterviewCoverageSummary);
